import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { LeafletStopPropagationDirective } from './toolbar/leaflet-stop-propagation.directive';
import { ToolbarHostComponent } from './toolbar/toolbar-host/toolbar-host.component';
import { ToolbarDrawerComponent } from './toolbar/toolbar-drawer/toolbar-drawer.component';
import { VesselTypeFilterComponent } from './toolbar/vessel-type-filter/vessel-type-filter.component';
import { PlaybackLoadingComponent } from './toolbar/playback-loading/playback-loading.component';
import { PlaybackDaterangeModalComponent } from './toolbar/playback-daterange-modal/playback-daterange-modal.component';
import { SeparatorControlComponent } from './toolbar/controls/toolbar-chrome-controls';
import {
  ClusterToggleControlComponent,
  CloseControlComponent,
  LabelsToggleControlComponent,
  PlayPauseControlComponent,
  SkipEndControlComponent,
  SkipStartControlComponent,
  SpeedControlComponent,
  StepBackControlComponent,
  StepForwardControlComponent,
  TimelineControlComponent,
} from './toolbar/controls/toolbar-transport-controls';
import {
  DateRangeControlComponent,
  HistoryEntryControlComponent,
  LiveBadgeControlComponent,
  PlaybackHistoryControlComponent,
  TypeFilterControlComponent,
} from './toolbar/controls/toolbar-live-controls';
import { TOOLBAR_CONTROL_PROVIDERS } from './toolbar/toolbar-controls.providers';
import { AisObservationsComponent } from './panels/ais-observations/ais-observations.component';

/**
 * THE AIS TOOLBAR CHROME every provider shares (D16).
 *
 * The container, its drawer, its dialogs, the sixteen provider-neutral
 * controls — the ones whose state and action both live on
 * `PlaybackController`, which is the test D9 settled for what belongs here —
 * and the one provider-neutral SIDEBAR section, Observations, which passes the
 * same test from the other end: its subject is the observation catalogue, which
 * the tip engine offers to every AIS vendor and lets none of them redefine.
 *
 * WHY A MODULE AND NOT A BARREL OF ARRAYS. `app.module.ts` imported 47 symbols
 * out of `ais/` across ~25 statements and listed 43 of them in `declarations`.
 * D9 and D13 both ended with "a new sensor edits nothing shared", and both were
 * true INSIDE `ais/` and false of `app.module.ts`, which still had to be
 * touched in three places. A module is the Angular-shaped answer: a provider
 * now ships one importable thing.
 *
 * EXPORTS ARE DELIBERATELY NARROW — five of twenty-three. The rest are either
 * mounted dynamically by `ToolbarHostComponent` through `NgComponentOutlet`
 * (which needs them DECLARED, not exported) or opened by class through
 * `MatDialog`. Exporting everything would be the easy call and would let a
 * consumer start depending on a control that is supposed to be reachable only
 * through the registry.
 */
const DECLARATIONS = [
  // Chrome
  LeafletStopPropagationDirective,
  // Sidebar. Not toolbar chrome, and the only panel section here — it renders
  // the observation catalogue, which is shared across every AIS vendor by the
  // tip engine's own design, and it names no provider.
  AisObservationsComponent,
  ToolbarHostComponent,
  ToolbarDrawerComponent,
  VesselTypeFilterComponent,
  // Dialogs — opened by class from the map component, never by selector.
  PlaybackLoadingComponent,
  PlaybackDaterangeModalComponent,
  // The sixteen registry controls. Declared so `NgComponentOutlet` can build
  // them; never referenced by selector anywhere.
  SeparatorControlComponent,
  CloseControlComponent,
  PlayPauseControlComponent,
  SkipStartControlComponent,
  StepBackControlComponent,
  TimelineControlComponent,
  StepForwardControlComponent,
  SkipEndControlComponent,
  SpeedControlComponent,
  LabelsToggleControlComponent,
  ClusterToggleControlComponent,
  TypeFilterControlComponent,
  DateRangeControlComponent,
  LiveBadgeControlComponent,
  HistoryEntryControlComponent,
  PlaybackHistoryControlComponent,
];

@NgModule({
  imports: [
    CommonModule,
    // `cdkDrag` on the toolbar host — the bar is draggable within the map.
    DragDropModule,
    MatIconModule,
    MatTooltipModule,
    // `mat-progress-spinner`, in the playback-loading dialog only.
    MatProgressSpinnerModule,
  ],
  declarations: DECLARATIONS,
  exports: [
    // The map component's own template mounts these two.
    ToolbarHostComponent,
    LeafletStopPropagationDirective,
    // A provider's controls render into the shared drawer and reuse the shared
    // type-filter list, so both cross the module boundary.
    ToolbarDrawerComponent,
    VesselTypeFilterComponent,
    // Mounted by selector from the map component's template, beside whichever
    // provider's own panel sections — the fifth export, and the first that is
    // not toolbar chrome.
    AisObservationsComponent,
  ],
  providers: [
    // The provider-neutral half of `TOOLBAR_CONTROLS`. A provider contributes
    // its own descriptors into the same `multi` token from its own module.
    ...TOOLBAR_CONTROL_PROVIDERS,
  ],
})
export class AisSharedModule {}
